"use client";
import { useAlgoliaClient } from "@hooks/useAlgoliaClient";
import { AND_SYMBOL } from "@lib/constants/filtersConstants";
import type { FC } from "react";
import { Configure } from "react-instantsearch";
import type { InitialResults, StateMapping } from "instantsearch.js";
import { getAlgoliaUserToken } from "@lib/analytics/getAlgoliaUserToken";
import { useStore } from "@store/index";
import React from "react";
import { InstantSearchNext } from "react-instantsearch-nextjs";
import { Box } from "@ui/components/layout/box/Box";
import { useIsHydrated } from "@hooks/useIsHydrated";

interface AlgoliaProviderProps {
    children: React.ReactNode;
    locale?: string;
    mode?: "products" | "stores";
    aroundLatLng?: string;
    facetFilters?: string[];
    hitsPerPage?: number;
    algoliaFallbackState?: InitialResults;
}

export const AlgoliaProvider: FC<AlgoliaProviderProps> = ({
    children,
    locale,
    mode,
    facetFilters,
    aroundLatLng,
    hitsPerPage,
}) => {
    const { indexName, searchClient } = useAlgoliaClient(locale, mode);
    const routing = {
        stateMapping: {
            stateToRoute(uiState) {
                const indexUiState = uiState[indexName] || {};
                const route = {
                    query: indexUiState.query,
                    sortBy: indexUiState.sortBy && indexUiState.sortBy.split(`${indexName}_`)[1],
                };

                const keys = Object.keys(indexUiState?.refinementList ?? {});

                keys?.forEach((facetName) => {
                    route[facetName] =
                        indexUiState?.refinementList &&
                        indexUiState?.refinementList[facetName] &&
                        indexUiState?.refinementList[facetName].join(AND_SYMBOL);
                });

                if (typeof window !== "undefined") {
                    const currentSearchParams = new URLSearchParams(window.location.search);

                    currentSearchParams.forEach((value, key) => {
                        // If this key is NOT already handled by Algolia (route), preserve it.
                        // This keeps utm_source, gclid, etc.
                        if (route[key] === undefined) {
                            route[key] = value;
                        }
                    });
                }

                return route;
            },

            routeToState(routeState) {
                const refinementList = {};
                const facetNames = Object.keys(routeState).filter(
                    (it) => it !== "sortBy" && it !== "page" && it !== "query"
                );

                facetNames?.forEach((facetName) => {
                    const item = routeState[facetName];
                    if (!item) return;
                    if (Array.isArray(item)) {
                        item.forEach((element) => {
                            if (element?.includes(AND_SYMBOL)) {
                                refinementList[facetName] = refinementList[facetName]
                                    ? [...refinementList[facetName], ...element.split(AND_SYMBOL)]
                                    : element.split(AND_SYMBOL);
                            } else {
                                refinementList[facetName] = refinementList[facetName]
                                    ? [...refinementList[facetName], element]
                                    : [element];
                            }
                        });
                    } else {
                        refinementList[facetName] = item.split(AND_SYMBOL);
                    }
                });

                return {
                    [indexName]: {
                        sortBy: routeState.sortBy && `${indexName}_${routeState.sortBy}`,
                        page: routeState.page,
                        query: routeState.query,
                        refinementList,
                    },
                };
            },
        } as StateMapping<any, any>,
    };

    const me = useStore((state) => state.data.me);

    const algoliaUserToken = getAlgoliaUserToken(me);

    const isHydrated = useIsHydrated();
    if (!isHydrated) {
        return <Box minBlockSize="25vh"></Box>;
    }

    return (
        <Box minBlockSize="25vh">
            <InstantSearchNext searchClient={searchClient} indexName={indexName} routing={routing}>
                <Configure
                    // @ts-ignore
                    hitsPerPage={hitsPerPage}
                    aroundLatLng={aroundLatLng}
                    clickAnalytics
                    facetFilters={facetFilters ?? ["hasStock:true"]}
                    userToken={algoliaUserToken}
                />
                {children}
            </InstantSearchNext>
        </Box>
    );
};

export default AlgoliaProvider;
